import { useState, useEffect, useRef, type MouseEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Input, Button, message, Tabs, Checkbox, Alert } from 'antd'
import { GiftOutlined, MailOutlined, LockOutlined, SafetyCertificateOutlined, SafetyOutlined } from '@ant-design/icons'
import { useAuthStore } from '../../stores/authStore'
import LegalTermsModal from '../../components/legal/LegalTermsModal'
import { login, register, sendCode, verifyLoginTwoFactor, verifyLoginEmail } from '../../services/auth'
import { getLegalConfig } from '../../services/config'
import { LEGAL_ROUTES, LEGAL_TERMS_VERSION } from '../../constants/legal'
import { LegalDocKey } from '../../constants/legalContent'
import TurnstileWidget, { type TurnstileWidgetHandle } from '../../components/common/TurnstileWidget'
import EmailCodeVerification from '../../components/common/EmailCodeVerification'
import { useEmailCodeVerification } from '../../hooks/useEmailCodeVerification'

interface LoginForm {
  email: string
  password: string
  totpCode?: string
  emailCode?: string
}

interface RegisterForm {
  email: string
  code: string
  password: string
  confirmPassword: string
  inviteCode?: string
  agreeToTerms: boolean
}

export default function Landing() {
  const [searchParams] = useSearchParams()
  const inviteCodeFromUrl =
    searchParams.get('code')?.trim() ||
    searchParams.get('inviteCode')?.trim() ||
    undefined
  const initialMode =
    searchParams.get('mode') === 'register' || inviteCodeFromUrl ? 'register' : 'login'
  const [activeTab, setActiveTab] = useState<string>(initialMode)
  const [loginLoading, setLoginLoading] = useState(false)
  const [registerLoading, setRegisterLoading] = useState(false)
  const [sendingCode, setSendingCode] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [legalTermsVersion, setLegalTermsVersion] = useState(LEGAL_TERMS_VERSION)
  const [legalModalOpen, setLegalModalOpen] = useState(false)
  const [activeLegalDoc, setActiveLegalDoc] = useState<LegalDocKey>('userAgreement')
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const [turnstileError, setTurnstileError] = useState<string | null>(null)
  const [loginVerificationMethod, setLoginVerificationMethod] = useState<'email' | 'totp' | null>(null)
  const [loginChallengeToken, setLoginChallengeToken] = useState<string | null>(null)
  const navigate = useNavigate()
  const setAuth = useAuthStore((state) => state.setAuth)
  const [loginForm] = Form.useForm<LoginForm>()
  const [registerForm] = Form.useForm<RegisterForm>()
  const registerEmail = Form.useWatch('email', registerForm)
  const emailVerification = useEmailCodeVerification(`register:${activeTab}:${registerEmail?.trim() ?? ''}`)
  const turnstileRef = useRef<TurnstileWidgetHandle | null>(null)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY
  const loginEmail = Form.useWatch('email', loginForm)
  const loginPassword = Form.useWatch('password', loginForm)

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  useEffect(() => {
    let cancelled = false
    getLegalConfig()
      .then((res) => {
        if (!cancelled && res.data?.legalTermsVersion) {
          setLegalTermsVersion(res.data.legalTermsVersion)
        }
      })
      .catch(() => {
        // Keep local fallback when config API is temporarily unavailable.
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (inviteCodeFromUrl) {
      registerForm.setFieldValue('inviteCode', inviteCodeFromUrl)
    }
  }, [inviteCodeFromUrl, registerForm])

  useEffect(() => {
    if (!loginChallengeToken) {
      return
    }
    setLoginChallengeToken(null)
    setLoginVerificationMethod(null)
    loginForm.setFieldValue('totpCode', undefined)
    loginForm.setFieldValue('emailCode', undefined)
    setTurnstileToken(null)
    setTurnstileError(null)
    turnstileRef.current?.reset()
  }, [loginEmail, loginPassword])

  const openLegalModal = (docKey: LegalDocKey) => {
    setActiveLegalDoc(docKey)
    setLegalModalOpen(true)
  }

  const handleLegalLinkClick = (event: MouseEvent<HTMLAnchorElement>, docKey: LegalDocKey) => {
    event.preventDefault()
    event.stopPropagation()
    openLegalModal(docKey)
  }

  const handleLogin = async (values: LoginForm) => {
    const challengeToken = loginChallengeToken
    if (!challengeToken && turnstileSiteKey && !turnstileToken) {
      const nextMessage = turnstileError ?? '请先完成人机验证'
      setTurnstileError(nextMessage)
      message.warning(nextMessage)
      return
    }
    setLoginLoading(true)
    try {
      if (challengeToken) {
        const res = loginVerificationMethod === 'email'
          ? await verifyLoginEmail(challengeToken, values.emailCode ?? '')
          : await verifyLoginTwoFactor(challengeToken, values.totpCode ?? '')
        if (!res.data.token || !res.data.merchant) {
          throw new Error('登录响应缺少认证信息')
        }
        setAuth(res.data.token, res.data.merchant)
        message.success('登入成功')
        navigate('/dashboard')
        return
      }

      const res = await login(values.email, values.password, turnstileToken || undefined)
      if ((res.data.requiresTwoFactor || res.data.requiresEmailVerification) && res.data.loginChallengeToken) {
        setLoginVerificationMethod(res.data.requiresTwoFactor ? 'totp' : 'email')
        setLoginChallengeToken(res.data.loginChallengeToken)
        loginForm.setFieldValue('totpCode', undefined)
        loginForm.setFieldValue('emailCode', undefined)
        message.info(res.data.requiresTwoFactor
          ? '账号已开启 2FA，请输入 TOTP 验证码继续登录'
          : '驗證碼已發送。如果沒有收到，請先檢查垃圾郵箱。')
        return
      }
      if (!res.data.token || !res.data.merchant) {
        throw new Error('登录响应缺少认证信息')
      }
      setAuth(res.data.token, res.data.merchant)
      message.success('登入成功')
      navigate('/dashboard')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '登入失敗')
      if (challengeToken) {
        setLoginChallengeToken(null)
        setLoginVerificationMethod(null)
        loginForm.setFieldValue('totpCode', undefined)
        loginForm.setFieldValue('emailCode', undefined)
        setTurnstileToken(null)
        setTurnstileError(null)
        turnstileRef.current?.reset()
      } else {
        turnstileRef.current?.reset()
      }
    } finally {
      setLoginLoading(false)
    }
  }

  const showLoginVerification = Boolean(loginChallengeToken)
  const showLoginTotpField = showLoginVerification && loginVerificationMethod === 'totp'
  const shouldRequireTurnstile = Boolean(!showLoginVerification && turnstileSiteKey)
  const loginDisabled = Boolean(shouldRequireTurnstile && !turnstileToken)

  const handleSendCode = async () => {
    try {
      await registerForm.validateFields(['email'])
      const email = registerForm.getFieldValue('email')
      if (!email) {
        message.error('請先輸入郵箱')
        return
      }
      setSendingCode(true)
      await emailVerification.send((token) => sendCode(email, 'register', token))
      message.success('驗證碼已發送。如果沒有收到，請先檢查垃圾郵箱。')
      setCountdown(60)
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '發送驗證碼失敗')
    } finally {
      setSendingCode(false)
    }
  }

  const handleRegister = async (values: RegisterForm) => {
    if (!emailVerification.ready) {
      message.warning('請先完成人機驗證')
      return
    }
    if (values.password !== values.confirmPassword) {
      message.error('兩次輸入的密碼不一致')
      return
    }
    setRegisterLoading(true)
    try {
      const res = await register(
        values.email,
        values.password,
        values.code,
        values.agreeToTerms,
        legalTermsVersion,
        values.inviteCode?.trim() || undefined,
      )
      if (!res.data.token || !res.data.merchant) {
        throw new Error('注册响应缺少认证信息')
      }
      setAuth(res.data.token, res.data.merchant)
      message.success('註冊成功')
      navigate('/dashboard')
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : '註冊失敗')
    } finally {
      setRegisterLoading(false)
    }
  }

  const features = [
    { icon: '🚀', title: '低門檻開戶', desc: '快速註冊，即刻開始收款' },
    { icon: '🛡️', title: '合規收款', desc: '標準化收單，安全可靠' },
    { icon: '💸', title: '快速結算', desc: 'KYC 通過後，靈活結款' },
  ]

  const tabItems = [
    {
      key: 'login',
      label: '登入',
      children: (
        <Form form={loginForm} layout="vertical" onFinish={handleLogin} className="auth-form">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '請輸入郵箱' },
              { type: 'email', message: '請輸入有效的郵箱地址' },
            ]}
          >
            <Input
              prefix={<MailOutlined className="input-icon" />}
              disabled={loginLoading}
              placeholder="郵箱地址"
              size="large"
              className="landing-input"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: '請輸入密碼' }]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              disabled={loginLoading}
              placeholder="密碼"
              size="large"
              className="landing-input"
            />
          </Form.Item>
          {showLoginVerification && loginVerificationMethod === 'email' && (
            <>
              <Alert type="info" showIcon style={{ marginBottom: 16 }}
                message="請驗證您的郵箱"
                description="驗證碼已發送至您的郵箱。如果沒有收到，請先檢查垃圾郵箱。登入驗證 5 分鐘內有效。"
              />
              <Form.Item name="emailCode" rules={[
                { required: true, message: '請輸入郵箱驗證碼' },
                { pattern: /^\d{6}$/, message: '請輸入 6 位數字驗證碼' },
              ]}>
                <Input prefix={<MailOutlined />} placeholder="請輸入 6 位郵箱驗證碼"
                  size="large" maxLength={6} inputMode="numeric" autoComplete="one-time-code" />
              </Form.Item>
            </>
          )}
          {showLoginTotpField && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="账号已开启 2FA"
              description="请输入身份验证器中的 6 位 TOTP 验证码完成登录。"
            />
          )}
          {showLoginTotpField && (
            <Form.Item
              name="totpCode"
              rules={[{ required: true, message: '請輸入 2FA 驗證碼' }, { pattern: /^\d{6}$/, message: '請輸入 6 位數字驗證碼' }]}
            >
              <Input
                prefix={<SafetyCertificateOutlined className="input-icon" />}
                placeholder="請輸入 6 位驗證碼"
                size="large"
                className="landing-input"
                maxLength={6}
              />
            </Form.Item>
          )}
          {!showLoginVerification && (turnstileSiteKey ? (
            <div style={{ marginBottom: 16 }}>
              <TurnstileWidget
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onTokenChange={setTurnstileToken}
                onErrorChange={setTurnstileError}
              />
            </div>
          ) : (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Turnstile 未配置"
              description="当前环境未设置 VITE_TURNSTILE_SITE_KEY，登录页不会显示 Cloudflare 机器人验证。"
            />
          ))}
          {!showLoginVerification && turnstileSiteKey && turnstileError && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              message="Turnstile 验证不可用"
              description={turnstileError}
            />
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loginLoading}
              disabled={loginDisabled}
              block
              size="large"
              className="landing-btn"
              data-testid="login-submit"
            >
              {showLoginVerification ? (showLoginTotpField ? '驗證 2FA' : '驗證郵箱') : '登入'}
            </Button>
          </Form.Item>
          {showLoginVerification && (
            <Button type="link" block disabled={loginLoading} onClick={() => {
              setLoginChallengeToken(null)
              setLoginVerificationMethod(null)
              loginForm.setFieldValue('totpCode', undefined)
              loginForm.setFieldValue('emailCode', undefined)
              setTurnstileToken(null)
              setTurnstileError(null)
              turnstileRef.current?.reset()
            }}>返回登入</Button>
          )}
          <div className="auth-footer-link">
            <a onClick={() => navigate('/reset-password')}>忘記密碼？</a>
          </div>
        </Form>
      ),
    },
    {
      key: 'register',
      label: '註冊',
      children: (
        <Form form={registerForm} layout="vertical" onFinish={handleRegister} className="auth-form">
          <Form.Item
            name="email"
            rules={[
              { required: true, message: '請輸入郵箱' },
              { type: 'email', message: '請輸入有效的郵箱地址' },
            ]}
          >
            <Input
              prefix={<MailOutlined className="input-icon" />}
              placeholder="郵箱地址"
              size="large"
              className="landing-input"
            />
          </Form.Item>
          <Form.Item
            name="code"
            rules={[{ required: true, message: '請輸入驗證碼' }]}
          >
            <div className="code-input-wrapper">
              <Input
                prefix={<SafetyOutlined className="input-icon" />}
                placeholder="驗證碼"
                size="large"
                className="landing-input code-input"
              />
              <Button
                onClick={handleSendCode}
                loading={sendingCode}
                disabled={countdown > 0 || !emailVerification.canSend}
                className="send-code-btn"
                data-testid="send-code-btn"
              >
                {countdown > 0 ? `${countdown}s` : '獲取驗證碼'}
              </Button>
            </div>
          </Form.Item>
          <Form.Item
            name="password"
            rules={[
              { required: true, message: '請輸入密碼' },
              { min: 8, message: '密碼至少8位' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="密碼（至少8位）"
              size="large"
              className="landing-input"
            />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            rules={[{ required: true, message: '請確認密碼' }]}
          >
            <Input.Password
              prefix={<LockOutlined className="input-icon" />}
              placeholder="確認密碼"
              size="large"
              className="landing-input"
            />
          </Form.Item>
          <Form.Item name="inviteCode">
            <Input
              prefix={<GiftOutlined className="input-icon" />}
              placeholder="邀請碼（可選）"
              size="large"
              className="landing-input"
            />
          </Form.Item>
          <Form.Item
            name="agreeToTerms"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error('請先同意相關法律條款')),
              },
            ]}
          >
            <Checkbox>
              我已閱讀並同意
              {' '}
              <a href={LEGAL_ROUTES.userAgreement} onClick={(event) => handleLegalLinkClick(event, 'userAgreement')}>
                《用戶協議》
              </a>
              {' '}
              <a href={LEGAL_ROUTES.privacyPolicy} onClick={(event) => handleLegalLinkClick(event, 'privacyPolicy')}>
                《隱私政策》
              </a>
              {' '}
              <a href={LEGAL_ROUTES.disclaimer} onClick={(event) => handleLegalLinkClick(event, 'disclaimer')}>
                《免責聲明》
              </a>
              {' '}
              與
              {' '}
              <a
                href={LEGAL_ROUTES.acceptableUsePolicy}
                onClick={(event) => handleLegalLinkClick(event, 'acceptableUsePolicy')}
              >
                《可接受使用政策》
              </a>
            </Checkbox>
          </Form.Item>
          {activeTab === 'register' && <EmailCodeVerification verification={emailVerification} />}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={registerLoading}
              disabled={!emailVerification.ready}
              block
              size="large"
              className="landing-btn"
              data-testid="register-submit"
            >
              註冊
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ]

  return (
    <div className="landing-container">
      {/* 神經網絡背景 */}
      <div className="landing-bg">
        <div className="neural-grid">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="neural-node" style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 4}s`,
            }} />
          ))}
        </div>
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
      </div>

      {/* 主內容區 */}
      <div className="landing-content">
        {/* 左側品牌區 */}
        <div className="landing-hero">
          <div className="hero-logo">
            <span className="logo-icon">⚡</span>
            <span className="logo-text">啟潤充值中心</span>
          </div>
          <h1 className="hero-title">
            面向中國出海 AI Startup
            <br />
            的收款與結算平台
          </h1>
          <p className="hero-subtitle">
            低門檻開戶，合規收款，快速結算
          </p>
          <div className="hero-features">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <span className="feature-icon">{feature.icon}</span>
                <div className="feature-content">
                  <div className="feature-title">{feature.title}</div>
                  <div className="feature-desc">{feature.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 右側登入/註冊區 */}
        <div className="landing-auth">
          <div className="auth-card">
            <Tabs
              activeKey={activeTab}
              onChange={(tab) => {
                emailVerification.reset()
                setActiveTab(tab)
              }}
              items={tabItems}
              centered
              className="auth-tabs"
            />
          </div>
        </div>
      </div>

      {/* 頁腳 */}
      <footer className="landing-footer">
        <span>© 2026 Kyren</span>
        <span className="footer-divider">·</span>
        <a href="mailto:support@kyrenpay.com">support@kyrenpay.com</a>
        <span className="footer-divider">·</span>
        <a href={LEGAL_ROUTES.userAgreement} onClick={(event) => handleLegalLinkClick(event, 'userAgreement')}>用戶協議</a>
        <span className="footer-divider">·</span>
        <a href={LEGAL_ROUTES.privacyPolicy} onClick={(event) => handleLegalLinkClick(event, 'privacyPolicy')}>隱私政策</a>
        <span className="footer-divider">·</span>
        <a href={LEGAL_ROUTES.disclaimer} onClick={(event) => handleLegalLinkClick(event, 'disclaimer')}>免責聲明</a>
        <span className="footer-divider">·</span>
        <a href={LEGAL_ROUTES.acceptableUsePolicy} onClick={(event) => handleLegalLinkClick(event, 'acceptableUsePolicy')}>AUP</a>
      </footer>

      <LegalTermsModal
        open={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        initialDocKey={activeLegalDoc}
        legalTermsVersion={legalTermsVersion}
      />
    </div>
  )
}
